import React, { useState } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Box,
  Avatar,
  Radio,
  RadioGroup,
  FormControlLabel,
} from "@material-ui/core";
import { styled } from "@material-ui/styles";
import { IStyledArguments } from "../../types/styled-arguments";
import { IPlayer } from "../../types";
import Button from "../button";
import Typography from "../typography";
import EmojiEventsIcon from "@material-ui/icons/EmojiEvents";
import { injectIntl } from "react-intl";
import messages from "./SelectMVPModal.intl";

const StyledDialog = styled(Dialog)(({ theme }: IStyledArguments) => ({
  "& .MuiDialog-paper": {
    borderRadius: "16px",
    padding: theme.spacing(2),
    maxWidth: "500px",
    width: "100%",
  },
}));

const StyledDialogTitle = styled(DialogTitle)(({ theme }: IStyledArguments) => ({
  display: "flex",
  alignItems: "center",
  gap: theme.spacing(1.5),
  paddingBottom: theme.spacing(2),
  "& svg": {
    color: theme.palette.primary.main,
    fontSize: "2rem",
  },
}));

const PlayerOption = styled(Box)(({ theme }: IStyledArguments) => ({
  display: "flex",
  alignItems: "center",
  padding: theme.spacing(2),
  marginBottom: theme.spacing(1),
  borderRadius: "12px",
  border: "2px solid transparent",
  transition: "all 0.3s ease",
  cursor: "pointer",
  "&:hover": {
    backgroundColor: theme.palette.extra.backgroundGrey,
    transform: "translateX(4px)",
  },
  "&.selected": {
    border: `2px solid ${theme.palette.primary.main}`,
    backgroundColor: theme.palette.extra.backgroundGrey,
  },
}));

const PlayerInfo = styled(Box)({
  display: "flex",
  alignItems: "center",
  gap: "16px",
  flex: 1,
});

const PlayerAvatar = styled(Avatar)(({ theme }: IStyledArguments) => ({
  width: "56px",
  height: "56px",
  background: theme.palette.primary.main,
  fontWeight: 700,
  fontSize: "1.2rem",
}));

const PlayerDetails = styled(Box)({
  display: "flex",
  flexDirection: "column",
  gap: "4px",
});

const GoalsChip = styled(Box)(({ theme }: IStyledArguments) => ({
  display: "flex",
  alignItems: "center",
  gap: theme.spacing(0.5),
  background: theme.palette.primary.main,
  color: "white",
  borderRadius: "20px",
  padding: theme.spacing(0.5, 1.5),
  fontWeight: 700,
  fontSize: "0.9rem",
}));

const getPlayerInitials = (player: IPlayer): string => {
  const firstInitial = player.name?.charAt(0)?.toUpperCase() || "";
  const lastInitial = player.surname?.charAt(0)?.toUpperCase() || "";
  return `${firstInitial}${lastInitial}`;
};

interface ISelectMVPModalProps {
  open: boolean;
  players: IPlayer[];
  scorerList: { [playerId: string]: number };
  currentMVP?: string;
  onClose: () => void;
  onConfirm: (playerId: string) => void;
  intl: any;
}

const SelectMVPModal = injectIntl<ISelectMVPModalProps>(
  ({ open, players, scorerList, currentMVP, onClose, onConfirm, intl }) => {
    const [selectedPlayer, setSelectedPlayer] = useState<string>(currentMVP || "");

    const handleConfirm = () => {
      if (selectedPlayer) {
        onConfirm(selectedPlayer);
        onClose();
      }
    };

    // Sort players by goals scored
    const sortedPlayers = [...players].sort((a, b) => {
      const goalsA = scorerList[a.id] || 0;
      const goalsB = scorerList[b.id] || 0;
      return goalsB - goalsA;
    });

    return (
      <StyledDialog open={open} onClose={onClose}>
        <StyledDialogTitle>
          <EmojiEventsIcon />
          <Typography.H5 style={{ margin: 0, fontWeight: 700 }}>
            {intl.formatMessage(messages.title)}
          </Typography.H5>
        </StyledDialogTitle>

        <DialogContent>
          <Typography.Body style={{ marginBottom: "20px", color: "#666" }}>
            {intl.formatMessage(messages.description)}
          </Typography.Body>

          <RadioGroup
            value={selectedPlayer}
            onChange={(e) => setSelectedPlayer(e.target.value)}
          >
            {sortedPlayers.map((player) => {
              const goals = scorerList[player.id] || 0;
              const isSelected = selectedPlayer === player.id;

              return (
                <FormControlLabel
                  key={player.id}
                  value={player.id}
                  control={<Radio style={{ display: "none" }} />}
                  label={
                    <PlayerOption className={isSelected ? "selected" : ""}>
                      <PlayerInfo>
                        <PlayerAvatar>
                          {getPlayerInitials(player)}
                        </PlayerAvatar>
                        <PlayerDetails>
                          <Typography.Body style={{ fontWeight: 700 }}>
                            {player.name} {player.surname}
                          </Typography.Body>
                          <Typography.Body style={{ fontSize: "0.85rem", color: "#666" }}>
                            {player.position || "Player"}
                            {player.number && ` • #${player.number}`}
                          </Typography.Body>
                        </PlayerDetails>
                      </PlayerInfo>
                      {goals > 0 && (
                        <GoalsChip>
                          {goals} ⚽
                        </GoalsChip>
                      )}
                    </PlayerOption>
                  }
                  style={{ margin: 0, width: "100%" }}
                />
              );
            })}
          </RadioGroup>
        </DialogContent>

        <DialogActions style={{ padding: "16px 24px" }}>
          <Button
            onClick={onClose}
            color="default"
            variant="outlined"
          >
            {intl.formatMessage(messages.cancel)}
          </Button>
          <Button
            onClick={handleConfirm}
            color="primary"
            variant="contained"
            disabled={!selectedPlayer}
          >
            {intl.formatMessage(messages.confirm)}
          </Button>
        </DialogActions>
      </StyledDialog>
    );
  }
);

export default SelectMVPModal;
